'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import classes from './NeumoArticles.module.scss';
import { IconArrowRight, IconCalendar, IconSparkles } from '@tabler/icons-react';
import { fetchArticles } from '@/src/helpers/fetch-articles';
import { Article } from '@/src/types/Header';

type NeumoArticlesProps = {
  badge?: string;
  title: string;
  description?: string;
  limit?: number;
  ctaLabel?: string;
  ctaHref?: string;
};

export const NeumoArticles = ({ data }: { data: NeumoArticlesProps }) => {
  const { badge, title, description, limit = 3, ctaLabel, ctaHref } = data;
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  // track image failures per article id/slug
  const [imgError, setImgError] = useState<Record<string, boolean>>({});

  const params = useParams();
  const lang = (params.lang as string) ?? 'fr';

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const result: any = await fetchArticles(1, '', lang);
        const articlesArray = Array.isArray(result) ? result : result?.articles || [];
        setArticles(articlesArray.slice(0, limit));
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [lang, limit]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const getKey = (article: any) =>
    String(article.id || article._id || article.title || Math.random());

  if (loading) {
    return (
      <section className={classes.section} aria-labelledby="articles-title">
        <div className={classes.container}>
          <div className={classes.header}>
            {badge && (
              <div className={classes.badgeWrapper}>
                <IconSparkles size={18} className={classes.badgeIcon} />
                <span className={classes.badge}>{badge}</span>
              </div>
            )}
            <h2 id="articles-title" className={classes.title}>
              {title}
            </h2>
            {description && <p className={classes.description}>{description}</p>}
          </div>
          <div className={classes.loading}>Chargement...</div>
        </div>
      </section>
    );
  }

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section className={classes.section} aria-labelledby="articles-title">
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && (
            <div className={classes.badgeWrapper}>
              <IconSparkles size={18} className={classes.badgeIcon} />
              <span className={classes.badge}>{badge}</span>
            </div>
          )}
          <h2 id="articles-title" className={classes.title}>
            {title}
          </h2>
          {description && <p className={classes.description}>{description}</p>}
        </div>

        <div className={classes.articlesGrid}>
          {articles.map((article) => {
            const articleSlug = article.slug || article.title?.replace(/\s+/g, '-') || '';
            const articleLink = `/${lang}/articles/${encodeURIComponent(articleSlug)}`;

            const key = getKey(article);
            const showImage = Boolean(article.mainImage) && !imgError[key];

            return (
              <article key={key} className={classes.articleCard}>
                <Link href={articleLink} className={classes.articleLink}>
                  <div className={classes.articleImageWrapper}>
                    {showImage ? (
                      <Image
                        src={article.mainImage as string}
                        alt={article.title || 'Article'}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className={classes.articleImage}
                        onError={() => setImgError((prev) => ({ ...prev, [key]: true }))}
                      />
                    ) : (
                      <div className={classes.imageFallback} aria-hidden="true" />
                    )}

                    <div className={classes.articleOverlay} />

                    {/* ✅ Clean meta bar: no overlap ever */}
                    <div className={classes.metaBar}>
                      <div className={classes.metaLeft}>
                        {article.createdAt && (
                          <div className={classes.articleDate}>
                            <IconCalendar size={14} />
                            <span>{formatDate(article.createdAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={classes.articleContent}>
                    <div className={classes.articleHeader}>
                      <h3 className={classes.articleTitle}>{article.title}</h3>
                    </div>

                    {article.tags && article.tags.length > 0 && (
                      <div className={classes.articleTagsBottom}>
                        {article.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className={classes.articleTag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {article.description && (
                      <p className={classes.articleDescription}>{article.description}</p>
                    )}

                    <div className={classes.articleFooter}>
                      <span className={classes.articleReadMore}>
                        Lire l&apos;article
                        <IconArrowRight size={16} className={classes.articleArrow} />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>

        {ctaLabel && ctaHref && (
          <div className={classes.ctaWrapper}>
            <Link href={ctaHref} className={classes.cta}>
              {ctaLabel}
              <IconArrowRight size={18} className={classes.ctaIcon} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
